import React from "react";
import { useAuth } from "../auth/authStore";
import { useWs } from "../ws/wsStore";
import { useToast } from "../ui/toast/ToastProvider";
import { chatApi } from "../api/chatApi";
import { chatConfig } from "./chatConfig";
import {
    CHAT_ACK_TIMEOUT_MS,
    dbg,
    uid,
    toDate,
    pickFirst,
    tryParseJSON,
    sanitizeText,
    messageKeyOf,
    pageVisible,
    normMember,
    mergeMembers,
    countOnline,
    normDialog,
    normReactions,
    normMessage,
    sortByUpdatedDesc,
    extractItems,
    mergeDialogSafe,
    mergeMessages,
    upsertReactionSummary,
    revokeLocalAttachments
} from "./useChat.helpers";
import { getVisibleChatText, parseChatContentCardPayload, parseChatStickerPayload } from "./contentPayloads";
import { buildReactionTogglePayload, REACTION_TARGET_TYPES } from "../ui/reactions/reactionModel";

const IMAGE_FILE_EXT_RE = /\.(avif|bmp|gif|heic|heif|jpe?g|png|svg|webp)$/i;

const CHAT_REACTION_EFFECT_EVENT = "chat:reaction-effect";

function pickReactionActorId(payload) {
    return (
        payload?.actorUserId ??
        payload?.actor_user_id ??
        payload?.reactedByUserId ??
        payload?.reacted_by_user_id ??
        payload?.fromUserId ??
        payload?.from_user_id ??
        payload?.senderUserId ??
        payload?.sender_user_id ??
        payload?.userId ??
        payload?.user_id ??
        payload?.authorId ??
        payload?.author_id ??
        null
    );
}

function pickReactionClientId(payload) {
    return payload?.clientId ?? payload?.client_id ?? payload?.presenceClientId ?? payload?.presence_client_id ?? null;
}

function pickReactionEmojiForFx(payload, prevReactions = [], nextReactions = []) {
    const directEmoji = payload?.emoji ?? payload?.reaction ?? payload?.e ?? payload?.reactionEmoji ?? payload?.reaction_emoji ?? null;
    const explicitAdd = payload?.add ?? payload?.added ?? payload?.isAdded ?? payload?.is_added ?? payload?.active ?? null;
    if (directEmoji && explicitAdd !== false) return String(directEmoji);

    const prevMap = new Map((Array.isArray(prevReactions) ? prevReactions : []).map((item) => [String(item?.emoji || ""), Number(item?.count) || 0]));
    for (const item of Array.isArray(nextReactions) ? nextReactions : []) {
        const emoji = String(item?.emoji || "");
        if (!emoji) continue;
        const nextCount = Number(item?.count) || 0;
        const prevCount = prevMap.get(emoji) || 0;
        if (nextCount > prevCount) return emoji;
    }
    return "";
}

function dispatchReactionEffect(payload) {
    if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") return;
    window.dispatchEvent(new CustomEvent(CHAT_REACTION_EFFECT_EVENT, { detail: payload }));
}

function isImageUploadFile(file) {
    const mime = String(file?.type || "").trim().toLowerCase();
    if (mime.startsWith("image/")) return true;
    const name = String(file?.name || "").trim().split("#")[0].split("?")[0];
    return IMAGE_FILE_EXT_RE.test(name);
}

function getAuthUserId(me) {
    const raw = me?.id ?? me?.userId ?? me?.user_id ?? me?.uid ?? me?.accountId ?? me?.account_id ?? null;
    const num = Number(raw);
    return Number.isFinite(num) && num > 0 ? num : raw ?? null;
}

function isPsychologistLikeRole(role) {
    const value = String(role || "").trim().toUpperCase().replace(/^ROLE_/, "");
    return value === "PSYCHOLOGIST" || value === "ADMIN" || value === "OWNER" || value === "HOST" || value === "MODERATOR";
}

export function useChat() {
    const { me, isAuthenticated } = useAuth();
    const { subscribe, publish, connected } = useWs();
    const toast = useToast();

    const meId = getAuthUserId(me);

    const [dialogs, setDialogs] = React.useState([]);
    const [activeDialogId, setActiveDialogId] = React.useState(null);
    const [dialogsLoading, setDialogsLoading] = React.useState(true);
    const [dialogsError, setDialogsError] = React.useState("");

    // ✅ details state (type/members/onlineCount...)
    const [dialogDetailsById, setDialogDetailsById] = React.useState(() => new Map());
    const detailsFetchedAtRef = React.useRef(new Map()); // did -> ts
    const presenceClientIdRef = React.useRef(uid()); // multi-tab friendly
    const activeDialogIdRef = React.useRef(null);
    const markMessagesReadRef = React.useRef(null);
    const readQueueRef = React.useRef(new Map()); // did -> Set(messageId)
    const readQueueTimersRef = React.useRef(new Map()); // did -> timer
    const dialogReadTimersRef = React.useRef(new Map()); // did -> timer
    const localReadDialogsRef = React.useRef(new Map()); // did -> ts, защищает UI от stale unread после refresh

    const [messagesByDialog, setMessagesByDialog] = React.useState(() => new Map());
    const [messagesLoading, setMessagesLoading] = React.useState(() => new Map());
    const [messagesCursor, setMessagesCursor] = React.useState(() => new Map());
    const [historyLoadedByDialog, setHistoryLoadedByDialog] = React.useState(() => new Map());
    const messagesByDialogRef = React.useRef(new Map());
    const pendingAckTimersRef = React.useRef(new Map());

    const typingTimeoutsRef = React.useRef(new Map());
    const dialogsRefreshTimerRef = React.useRef(null);

    const [typingUsersByDialog, setTypingUsersByDialog] = React.useState(() => new Map());
    const [typingUsersMetaByDialog, setTypingUsersMetaByDialog] = React.useState(() => new Map());
    const typingUserTimeoutsRef = React.useRef(new Map());

    // outbound typing control
    const typingStopTimersRef = React.useRef(new Map());
    const typingLastSentRef = React.useRef(new Map());
    const typingOnRef = React.useRef(new Map());

    const viewTimerRef = React.useRef(null);
    const lastViewRef = React.useRef({ did: null, active: null, visible: null, ts: 0 });

    React.useEffect(() => {
        messagesByDialogRef.current = messagesByDialog;
    }, [messagesByDialog]);

    React.useEffect(() => {
        activeDialogIdRef.current = activeDialogId;
    }, [activeDialogId]);

    React.useEffect(() => {
        const readQueueTimers = readQueueTimersRef.current;
        const dialogReadTimers = dialogReadTimersRef.current;
        const readQueue = readQueueRef.current;
        const localReadDialogs = localReadDialogsRef.current;

        return () => {
            for (const timer of readQueueTimers.values()) clearTimeout(timer);
            for (const timer of dialogReadTimers.values()) clearTimeout(timer);
            readQueueTimers.clear();
            dialogReadTimers.clear();
            readQueue.clear();
            localReadDialogs.clear();
        };
    }, []);

    const getPendingAckKey = React.useCallback((dialogId, clientId) => `${String(dialogId)}:${String(clientId)}`, []);

    const clearPendingAck = React.useCallback((dialogId, clientId) => {
        if (!dialogId || !clientId) return;
        const key = getPendingAckKey(dialogId, clientId);
        const timer = pendingAckTimersRef.current.get(key);
        if (timer) {
            clearTimeout(timer);
            pendingAckTimersRef.current.delete(key);
        }
    }, [getPendingAckKey]);

    const setLoadingFor = React.useCallback((dialogId, v) => {
        setMessagesLoading((prev) => {
            const n = new Map(prev);
            n.set(String(dialogId), Boolean(v));
            return n;
        });
    }, []);

    // ✅ UI helper: локально пропатчить сообщение по id/clientId/messageKey
    const patchMessageLocal = React.useCallback((dialogId, keyOrId, patchOrUpdater) => {
        const did = String(dialogId);
        const k = keyOrId == null ? null : String(keyOrId);
        if (!k) return;

        setMessagesByDialog((prev) => {
            const n = new Map(prev);
            const arr = n.get(did) || [];
            if (!arr.length) return prev;

            const ix = arr.findIndex((m) => {
                if (m?.id != null && String(m.id) === k) return true;
                if (m?.clientId != null && String(m.clientId) === k) return true;
                return String(messageKeyOf(m)) === k;
            });

            if (ix === -1) return prev;

            const cur = arr[ix];
            const nextMsg =
                typeof patchOrUpdater === "function"
                    ? patchOrUpdater(cur)
                    : { ...cur, ...(patchOrUpdater || {}) };

            const copy = [...arr];
            copy[ix] = nextMsg;
            n.set(did, copy);
            return n;
        });
    }, []);

    const getMessageSnapshot = React.useCallback((dialogId, keyOrId) => {
        const did = String(dialogId);
        const key = keyOrId == null ? "" : String(keyOrId);
        if (!did || !key) return null;

        const arr = messagesByDialogRef.current.get(did) || [];
        const hit = arr.find((m) => {
            if (m?.id != null && String(m.id) === key) return true;
            if (m?.clientId != null && String(m.clientId) === key) return true;
            return String(messageKeyOf(m)) === key;
        });

        return hit ? { ...hit } : null;
    }, []);

    // ------------------------------
    // ✅ Dialog Details load + Presence patch
    // ------------------------------

    const upsertDialogDetails = React.useCallback((raw) => {
        const d = normDialog(raw);
        if (!d) return;
        const did = String(d.id);

        setDialogDetailsById((prev) => {
            const n = new Map(prev);
            const cur = n.get(did);
            n.set(did, mergeDialogSafe(cur, d));
            return n;
        });

        // также обновим preview list (чтобы type/title/locked подтянулись)
        setDialogs((prev) => {
            const idx = prev.findIndex((x) => x.id === did);
            if (idx === -1) return prev;
            const next = [...prev];
            next[idx] = mergeDialogSafe(next[idx], d);
            return next.sort(sortByUpdatedDesc);
        });
    }, []);

    const loadDialogDetails = React.useCallback(async (dialogId, { force = false } = {}) => {
        const did = String(dialogId);
        if (!did) return;

        const now = Date.now();
        const last = detailsFetchedAtRef.current.get(did) || 0;

        // не дергаем слишком часто
        if (!force && now - last < 15_000) return;

        detailsFetchedAtRef.current.set(did, now);

        try {
            if (typeof chatApi.getDialog === "function") {
                const details = await chatApi.getDialog(did);
                upsertDialogDetails(details);

                // если members не пришли — пробуем отдельным запросом
                const nd = normDialog(details);
                const hasMembers = Array.isArray(nd?.members) && nd.members.length > 0;

                if (!hasMembers && typeof chatApi.listMembers === "function") {
                    try {
                        const members = await chatApi.listMembers(did);
                        upsertDialogDetails({ id: did, chatId: did, members });
                    } catch {
                        // ok
                    }
                }
            } else if (typeof chatApi.listMembers === "function") {
                // fallback: нет getDialog — хотя бы members
                try {
                    const members = await chatApi.listMembers(did);
                    upsertDialogDetails({ id: did, chatId: did, members });
                } catch {
                    // ok
                }
            }
        } catch {
            // молча: UI живёт от preview
        }
    }, [upsertDialogDetails]);

    const applyPresencePatch = React.useCallback((dialogId, patchUsers) => {
        const did = String(dialogId);
        const patch = (Array.isArray(patchUsers) ? patchUsers : []).map(normMember).filter(Boolean);
        if (!patch.length) return;

        // details: members + onlineCount
        setDialogDetailsById((prev) => {
            const n = new Map(prev);
            const cur = n.get(did) || { id: did };

            const prevMembers = Array.isArray(cur.members) ? cur.members : [];
            const nextMembers = mergeMembers(prevMembers, patch);

            const onlineCount = countOnline(nextMembers);
            const membersCount = nextMembers.length;

            n.set(did, {
                ...cur,
                members: nextMembers,
                onlineCount,
                membersCount: cur.membersCount ?? membersCount,
            });

            return n;
        });

        // preview list: для DIRECT обновим partner online/lastSeenAt
        setDialogs((prev) => {
            const idx = prev.findIndex((d) => d.id === did);
            if (idx === -1) return prev;

            const cur = prev[idx];
            const partnerId = cur.partnerUserId != null ? String(cur.partnerUserId) : null;
            if (!partnerId) return prev;

            const hit = patch.find((u) => String(u.userId ?? u.id) === partnerId);
            if (!hit) return prev;

            const next = [...prev];
            next[idx] = {
                ...cur,
                online: typeof hit.isOnline === "boolean" ? hit.isOnline : cur.online,
                lastSeenAt: hit.lastSeenAt instanceof Date ? hit.lastSeenAt : cur.lastSeenAt,
            };
            return next;
        });
    }, []);

    /**
     * ✅ NEW: per-message read
     * Вызывается из MessageList (IntersectionObserver) с ids сообщений, которые реально видны.
     * Важно: помечаем ТОЛЬКО сообщения собеседника как прочитанные МОЕЙ стороной.
     */
    const markMessagesRead = React.useCallback(
        async ({ dialogId, messageIds }) => {
            const did = String(dialogId);
            const ids = Array.from(
                new Set((Array.isArray(messageIds) ? messageIds : [])
                    .map((x) => Number(x))
                    .filter((x) => Number.isFinite(x) && x > 0))
            );
            if (!did || ids.length === 0) return;

            const myFlag = isPsychologistLikeRole(me?.role) ? "isReadByPsychologist" : "isReadByClient";

            // локально посчитаем, сколько реально "снимаем" из unreadCount
            const curArr = messagesByDialog.get(did) || [];
            const unreadSet = new Set(
                curArr
                    .filter((m) => {
                        const fromOther = meId != null && m?.fromUserId != null && Number(m.fromUserId) !== Number(meId);
                        if (!fromOther) return false;
                        if (m?.deleted || m?.isDeleted) return false;
                        return m?.[myFlag] !== true;
                    })
                    .map((m) => Number(m.id))
                    .filter((x) => Number.isFinite(x) && x > 0)
            );

            const dec = ids.reduce((acc, id) => (unreadSet.has(id) ? acc + 1 : acc), 0);

            if (dec > 0) {
                setDialogs((prev) =>
                    prev.map((d) =>
                        d.id === did
                            ? {
                                ...d,
                                unreadCount:
                                    typeof d.unreadCount === "number" ? Math.max(0, (d.unreadCount || 0) - dec) : d.unreadCount,
                            }
                            : d
                    )
                );
            }

            // optimistic: проставляем МОЙ флаг на конкретные сообщения собеседника
            setMessagesByDialog((prev) => {
                const n = new Map(prev);
                const arr = n.get(did) || [];
                if (!arr.length) return prev;

                const idsSet = new Set(ids);

                const next = arr.map((m) => {
                    const mid = Number(m?.id);
                    if (!Number.isFinite(mid) || mid <= 0) return m;
                    if (!idsSet.has(mid)) return m;

                    const fromOther = meId != null && m?.fromUserId != null && Number(m.fromUserId) !== Number(meId);
                    if (!fromOther) return m;

                    if (m?.deleted || m?.isDeleted) return m;
                    if (m?.[myFlag] === true) return m;

                    return { ...m, [myFlag]: true, readByMe: true };
                });

                n.set(did, next);
                return n;
            });

            const publishReadViaWs = () => {
                if (!connected || typeof publish !== "function") return false;
                const didNumber = Number(did);
                const readerIdNumber = Number(meId);
                const payload = {
                    chatId: Number.isFinite(didNumber) ? didNumber : did,
                    dialogId: Number.isFinite(didNumber) ? didNumber : did,
                    messageIds: ids,
                    messageId: ids[ids.length - 1],
                    upToMessageId: ids[ids.length - 1],
                    readAt: new Date().toISOString(),
                };

                if (Number.isFinite(readerIdNumber) && readerIdNumber > 0) {
                    payload.fromUserId = readerIdNumber;
                    payload.readerUserId = readerIdNumber;
                    payload.userId = readerIdNumber;
                }

                return publish(chatConfig.read, payload);
            };

            publishReadViaWs();

            // server call (REST)
            try {
                if (typeof chatApi.markMessagesRead === "function") {
                    await chatApi.markMessagesRead(did, ids);
                }
            } catch {
                publishReadViaWs();
                try {
                    if (typeof chatApi.markDialogRead === "function") await chatApi.markDialogRead(did);
                } catch {}
                // не откатываем — refreshDialogsDebounced/листинг выровняет
            }
        },
        [connected, me?.role, meId, messagesByDialog, publish]
    );

    React.useEffect(() => {
        markMessagesReadRef.current = markMessagesRead;
    }, [markMessagesRead]);

    const queueReadMark = React.useCallback((dialogId, messageIds, delay = 180) => {
        const did = dialogId == null ? "" : String(dialogId).trim();
        const ids = Array.from(
            new Set((Array.isArray(messageIds) ? messageIds : [])
                .map((x) => Number(x))
                .filter((x) => Number.isFinite(x) && x > 0))
        );
        if (!did || !ids.length) return;

        const current = readQueueRef.current.get(did) || new Set();
        ids.forEach((id) => current.add(id));
        readQueueRef.current.set(did, current);

        const prevTimer = readQueueTimersRef.current.get(did);
        if (prevTimer) clearTimeout(prevTimer);

        const timer = setTimeout(() => {
            const queued = Array.from(readQueueRef.current.get(did) || []);
            readQueueRef.current.delete(did);
            readQueueTimersRef.current.delete(did);
            if (!queued.length) return;
            markMessagesReadRef.current?.({ dialogId: did, messageIds: queued });
        }, delay);

        readQueueTimersRef.current.set(did, timer);
    }, []);

    const queueDialogReadMark = React.useCallback((dialogId, delay = 320) => {
        const did = dialogId == null ? "" : String(dialogId).trim();
        if (!did) return;

        localReadDialogsRef.current.set(did, Date.now());

        const prevTimer = dialogReadTimersRef.current.get(did);
        if (prevTimer) clearTimeout(prevTimer);

        const timer = setTimeout(async () => {
            dialogReadTimersRef.current.delete(did);
            localReadDialogsRef.current.set(did, Date.now());

            try {
                if (connected && typeof publish === "function") {
                    const didNumber = Number(did);
                    const readerIdNumber = Number(meId);
                    const payload = {
                        chatId: Number.isFinite(didNumber) ? didNumber : did,
                        dialogId: Number.isFinite(didNumber) ? didNumber : did,
                        readAt: new Date().toISOString(),
                        readAll: true,
                    };
                    if (Number.isFinite(readerIdNumber) && readerIdNumber > 0) {
                        payload.fromUserId = readerIdNumber;
                        payload.readerUserId = readerIdNumber;
                        payload.userId = readerIdNumber;
                    }
                    publish(chatConfig.read, payload);
                }
            } catch {}

            try {
                if (typeof chatApi.markDialogRead === "function") {
                    await chatApi.markDialogRead(did);
                }
            } catch {}
        }, delay);

        dialogReadTimersRef.current.set(did, timer);
    }, [connected, meId, publish]);

    /**
     * ✅ legacy (оставил, чтобы не ломать старый UI)
     * Но для правильной логики лучше использовать markMessagesRead.
     */
    const markDialogReadSeen = React.useCallback(
        async (dialogId) => {
            const did = String(dialogId);
            localReadDialogsRef.current.set(did, Date.now());
            try {
                if (typeof chatApi.markDialogRead === "function") {
                    await chatApi.markDialogRead(did);
                }

                setDialogs((prev) => prev.map((d) => (d.id === did ? { ...d, unreadCount: 0 } : d)));

                const myFlag = isPsychologistLikeRole(me?.role) ? "isReadByPsychologist" : "isReadByClient";

                setMessagesByDialog((prev) => {
                    const n = new Map(prev);
                    const arr = n.get(did) || [];
                    if (!arr.length) return prev;

                    const next = arr.map((m) => {
                        const fromOther = meId != null && m?.fromUserId != null && Number(m.fromUserId) !== Number(meId);
                        if (!fromOther) return m;
                        if (m?.deleted || m?.isDeleted) return m;
                        if (m?.[myFlag] === true) return m;
                        return { ...m, [myFlag]: true, readByMe: true };
                    });

                    n.set(did, next);
                    return n;
                });
            } catch {
                // молча
            }
        },
        [me?.role, meId]
    );

    const refreshDialogsDebounced = React.useCallback(() => {
        if (dialogsRefreshTimerRef.current) return;
        dialogsRefreshTimerRef.current = setTimeout(async () => {
            dialogsRefreshTimerRef.current = null;
            try {
                const raw = await chatApi.listDialogs();
                const incoming = (raw || []).map(normDialog).filter(Boolean);

                setDialogs((prev) => {
                    const map = new Map(prev.map((d) => [d.id, d]));
                    const now = Date.now();
                    const activeDid = activeDialogIdRef.current == null ? "" : String(activeDialogIdRef.current);
                    const merged = incoming.map((d) => {
                        const next = mergeDialogSafe(map.get(d.id), d);
                        const readTs = localReadDialogsRef.current.get(String(next?.id));
                        // Backend может вернуть stale unread сразу после WS-сообщения.
                        // Для открытого диалога не даём refresh вернуть бейдж назад, пока read-запрос доходит до сервера.
                        if (next && activeDid && String(next.id) === activeDid && readTs && now - readTs < 15000) {
                            return { ...next, unreadCount: 0, unread: 0 };
                        }
                        return next;
                    });
                    for (const p of prev) if (!merged.some((x) => x.id === p.id)) merged.push(p);
                    return merged.sort(sortByUpdatedDesc);
                });
            } catch {}
        }, 300);
    }, []);

    const upsertDialog = React.useCallback((raw) => {
        const d = normDialog(raw);
        if (!d) return;
        setDialogs((prev) => {
            const idx = prev.findIndex((x) => x.id === d.id);
            if (idx === -1) return [...prev, d].sort(sortByUpdatedDesc);
            const merged = mergeDialogSafe(prev[idx], d);
            const next = [...prev];
            next[idx] = merged;
            return next.sort(sortByUpdatedDesc);
        });
    }, []);

    const setTyping = React.useCallback((dialogId, isTyping) => {
        const did = String(dialogId);

        setDialogs((prev) => {
            const idx = prev.findIndex((x) => x.id === did);
            if (idx === -1) return prev;

            const next = [...prev];
            next[idx] = { ...next[idx], typing: Boolean(isTyping) };
            return next;
        });
    }, []);

    const applyEdit = React.useCallback((dialogId, messageId, patch) => {
        const did = String(dialogId);
        const mid = messageId == null ? null : String(messageId);
        if (!mid) return;

        setMessagesByDialog((prev) => {
            const n = new Map(prev);
            const arr = n.get(did) || [];
            const ix = arr.findIndex((m) => String(m?.id ?? "") === mid || (m?.clientId && String(m.clientId) === mid));
            if (ix === -1) return prev;
            const copy = [...arr];
            copy[ix] = { ...copy[ix], ...patch };
            n.set(did, copy);
            return n;
        });
    }, []);

    const applyEditByClientId = React.useCallback((dialogId, clientId, patch) => {
        const did = String(dialogId);
        if (!clientId) return;

        setMessagesByDialog((prev) => {
            const n = new Map(prev);
            const arr = n.get(did) || [];
            const ix = arr.findIndex((m) => m?.clientId && String(m.clientId) === String(clientId));
            if (ix === -1) return prev;
            const copy = [...arr];
            copy[ix] = { ...copy[ix], ...patch };
            n.set(did, copy);
            return n;
        });
    }, []);

    const applyDelete = React.useCallback((dialogId, messageId) => {
        const did = String(dialogId);
        const mid = messageId == null ? null : String(messageId);
        if (!mid) return;

        setMessagesByDialog((prev) => {
            const n = new Map(prev);
            const arr = n.get(did) || [];
            const ix = arr.findIndex((m) => m.id === mid || (m.clientId && m.clientId === mid));
            if (ix === -1) return prev;
            const copy = [...arr];
            clearPendingAck(did, copy[ix]?.clientId);
            revokeLocalAttachments(copy[ix]);
            copy[ix] = {
                ...copy[ix],
                deleted: true,
                isDeleted: true,
                deletedAt: new Date().toISOString(),
                deletedWhen: new Date().toISOString(),
                text: "Сообщение удалено",
                content: "Сообщение удалено",
                attachments: [],
                reactions: [],
                replyTo: null,
                replyToKey: null,
                replyToMessageId: null,
                replyToClientId: null,
            };

            n.set(did, copy);
            return n;
        });
    }, [clearPendingAck]);

    const applyReactionsSet = React.useCallback((dialogId, messageId, reactions) => {
        const did = String(dialogId);
        const mid = messageId == null ? null : String(messageId);
        if (!mid) return;

        setMessagesByDialog((prev) => {
            const n = new Map(prev);
            const arr = n.get(did) || [];
            const ix = arr.findIndex((m) => m.id === mid || (m.clientId && m.clientId === mid));
            if (ix === -1) return prev;
            const copy = [...arr];
            copy[ix] = { ...copy[ix], reactions: Array.isArray(reactions) ? reactions : [] };
            n.set(did, copy);
            return n;
        });
    }, []);

    const applyReactionOptimistic = React.useCallback((dialogId, messageId, emoji, add) => {
        const did = String(dialogId);
        const mid = messageId == null ? null : String(messageId);
        if (!mid) return { did, mid, prev: null, next: null };

        let snapshotPrev = null;

        setMessagesByDialog((prev) => {
            const n = new Map(prev);
            const arr = n.get(did) || [];
            const ix = arr.findIndex((m) => m.id === mid || (m.clientId && m.clientId === mid));
            if (ix === -1) return prev;

            const cur = arr[ix];
            snapshotPrev = cur?.reactions || [];

            const already = (cur.reactions || []).find((r) => r.emoji === emoji);
            const alreadyMe = Boolean(already?.me);

            const realAdd = add != null ? Boolean(add) : !alreadyMe;
            const delta = realAdd ? +1 : -1;
            const nextMe = realAdd ? true : false;

            const nextReactions = upsertReactionSummary(cur.reactions || [], emoji, nextMe, delta);

            const copy = [...arr];
            copy[ix] = { ...cur, reactions: nextReactions };
            n.set(did, copy);
            return n;
        });

        return { did, mid, prev: snapshotPrev };
    }, []);

    const appendMessage = React.useCallback(
        (msg) => {
            if (!msg?.dialogId) return;
            const did = String(msg.dialogId);
            const fromOther = meId != null && msg.fromUserId != null && Number(msg.fromUserId) !== Number(meId);
            const isActiveDialog = activeDialogIdRef.current != null && String(activeDialogIdRef.current) === did;
            const myFlag = isPsychologistLikeRole(me?.role) ? "isReadByPsychologist" : "isReadByClient";
            const messageForStore = fromOther && isActiveDialog
                ? { ...msg, [myFlag]: true, readByMe: true }
                : msg;

            setMessagesByDialog((prev) => {
                const n = new Map(prev);
                const cur = n.get(did) || [];

                if (messageForStore.id && cur.some((x) => x.id === messageForStore.id)) return prev;

                if (messageForStore.clientId) {
                    const ix = cur.findIndex((x) => x.clientId && x.clientId === messageForStore.clientId);
                    if (ix >= 0) {
                        const copy = [...cur];
                        clearPendingAck(did, messageForStore.clientId);
                        revokeLocalAttachments(copy[ix]);
                        copy[ix] = { ...copy[ix], ...messageForStore, status: messageForStore.status || "sent" };
                        n.set(did, copy);
                        return n;
                    }
                }

                // Fallback-дедуп: сервер не вернул clientId, но пришло подтверждение моего pending.
                if (!messageForStore.clientId && messageForStore.id && meId != null && messageForStore.fromUserId != null && Number(messageForStore.fromUserId) === Number(meId)) {
                    const msgTs = toDate(messageForStore.createdAt)?.getTime() || 0;
                    const ixPending = cur.findIndex((x) => {
                        if (!x?.clientId) return false;
                        if (x?.status !== "pending") return false;
                        if (x?.fromUserId == null || Number(x.fromUserId) !== Number(meId)) return false;

                        const sameText = String(x?.text || "") === String(messageForStore?.text || "");
                        const sameAttCount =
                            (Array.isArray(x?.attachments) ? x.attachments.length : 0) ===
                            (Array.isArray(messageForStore?.attachments) ? messageForStore.attachments.length : 0);
                        const xTs = toDate(x?.createdAt)?.getTime() || 0;
                        const closeByTime = Math.abs(msgTs - xTs) <= 20000;
                        return sameText && sameAttCount && closeByTime;
                    });

                    if (ixPending >= 0) {
                        const copy = [...cur];
                        clearPendingAck(did, copy[ixPending].clientId);
                        revokeLocalAttachments(copy[ixPending]);
                        copy[ixPending] = { ...copy[ixPending], ...messageForStore, status: messageForStore.status || "sent" };
                        n.set(did, copy);
                        return n;
                    }
                }

                const nextArr = [...cur, messageForStore].sort(
                    (a, b) => (toDate(a.createdAt)?.getTime() || 0) - (toDate(b.createdAt)?.getTime() || 0)
                );
                n.set(did, nextArr);
                return n;
            });

            upsertDialog({
                id: did,
                lastMessage: msg.text || (msg.attachments?.length ? "Вложение" : ""),
                lastMessageSender: msg.fromUserName || msg.fromName || undefined,
                updatedAt: toDate(msg.createdAt) || new Date(),
                ...(fromOther && isActiveDialog ? { unreadCount: 0 } : {}),
            });

            if (fromOther) setTyping(did, false);

            // Когда диалог уже открыт, не держим бейдж непрочитанных в sidebar до следующего refresh
            // и сразу отправляем batched read на backend/WS, даже если scroll-check ещё не успел сработать.
            if (fromOther && isActiveDialog) {
                localReadDialogsRef.current.set(did, Date.now());
                setDialogs((prev) =>
                    prev.map((d) => (String(d.id) === did ? { ...d, unreadCount: 0, unread: 0 } : d))
                );

                const messageId = Number(msg?.id ?? msg?.messageId);
                if (Number.isFinite(messageId) && messageId > 0) {
                    queueReadMark(did, [messageId]);
                }
                // Важный fallback: некоторые backend-события приходят без id сообщения
                // или read endpoint ожидает mark-dialog-read, а не список messageIds.
                queueDialogReadMark(did);
            }
        },
        [upsertDialog, meId, me?.role, setTyping, clearPendingAck, queueReadMark, queueDialogReadMark]
    );

    // initial dialogs load
    React.useEffect(() => {
        let alive = true;
        (async () => {
            if (!isAuthenticated) {
                setDialogs([]);
                setActiveDialogId(null);
                setDialogsLoading(false);
                return;
            }
            try {
                setDialogsLoading(true);
                setDialogsError("");

                const raw = await chatApi.listDialogs();
                if (!alive) return;

                const incoming = (raw || []).map(normDialog).filter(Boolean);

                setDialogs((prev) => {
                    if (!prev.length) return incoming.sort(sortByUpdatedDesc);
                    const map = new Map(prev.map((d) => [d.id, d]));
                    const merged = incoming.map((d) => mergeDialogSafe(map.get(d.id), d));
                    for (const p of prev) if (!merged.some((x) => x.id === p.id)) merged.push(p);
                    return merged.sort(sortByUpdatedDesc);
                });

                setActiveDialogId((cur) => (cur && incoming.some((d) => d.id === String(cur)) ? String(cur) : null));
            } catch (e) {
                if (!alive) return;
                setDialogsError(e?.message || "Не удалось загрузить диалоги");
                setDialogs([]);
                setActiveDialogId(null);
            } finally {
                if (alive) setDialogsLoading(false);
            }
        })();

        return () => {
            alive = false;
        };
    }, [isAuthenticated]);

    const loadMessages = React.useCallback(
        async (dialogId, { append = false } = {}) => {
            const did = String(dialogId);
            try {
                setLoadingFor(did, true);

                const cursor = append ? messagesCursor.get(did) : null;
                const res = await chatApi.listMessages(did, { limit: 30, cursor });

                const rawItems = extractItems(res);
                const incoming = (rawItems || []).map((x) => normMessage(x, did, meId)).filter(Boolean);

                setMessagesByDialog((prev) => {
                    const n = new Map(prev);
                    const cur = n.get(did) || [];

                    if (!append) {
                        const merged = mergeMessages(cur, incoming);
                        n.set(did, merged);
                    } else {
                        const merged = mergeMessages([...incoming, ...cur], []);
                        n.set(did, merged);
                    }

                    return n;
                });

                setMessagesCursor((prev) => {
                    const n = new Map(prev);
                    n.set(did, res?.nextCursor ?? res?.next_cursor ?? null);
                    return n;
                });

                if (!append) {
                    setHistoryLoadedByDialog((prev) => {
                        const n = new Map(prev);
                        n.set(did, true);
                        return n;
                    });
                }
            } catch (e) {
                toast.error(e?.message || "Не удалось загрузить сообщения");
            } finally {
                setLoadingFor(did, false);
            }
        },
        [setLoadingFor, messagesCursor, toast, meId]
    );

    React.useEffect(() => {
        if (!activeDialogId) return;
        const did = String(activeDialogId);

        const loaded = historyLoadedByDialog.get(did) === true;
        if (!loaded) loadMessages(did, { append: false });

        // ✅ also load details when opening
        loadDialogDetails(did, { force: false });
    }, [activeDialogId, loadMessages, historyLoadedByDialog, loadDialogDetails]);

    // presence polling fallback
    React.useEffect(() => {
        if (!isAuthenticated) return;
        const t = setInterval(() => refreshDialogsDebounced(), 15000);
        return () => clearInterval(t);
    }, [isAuthenticated, refreshDialogsDebounced]);

    // ✅ Presence WS for active dialog (join/leave + topic subscription)
    React.useEffect(() => {
        if (!isAuthenticated) return;
        if (!connected) return;
        if (!activeDialogId) return;
        if (typeof subscribe !== "function") return;

        const did = String(activeDialogId);
        const topic = typeof chatConfig.presenceSub === "function" ? chatConfig.presenceSub(did) : null;
        const presenceClientId = presenceClientIdRef.current;
        if (!topic) return;

        const handlePresence = (payload, msg) => {
            let obj = payload;
            if (obj && typeof obj === "object" && typeof obj.body === "string") obj = tryParseJSON(obj.body);
            if (typeof obj === "string") obj = tryParseJSON(obj);
            if ((!obj || typeof obj !== "object") && msg?.body) obj = tryParseJSON(msg.body);
            if (!obj || typeof obj !== "object") return;

            // snapshot: { users:[...] } or { members:[...] }
            const usersArr = obj.users || obj.members || obj.participants || obj.items || null;
            if (Array.isArray(usersArr)) {
                applyPresencePatch(did, usersArr);
                return;
            }

            // single: { userId, online/isOnline, lastSeenAt }
            const userId = obj.userId ?? obj.id ?? obj.memberId ?? obj.user_id ?? obj.member_id ?? null;
            if (userId == null) return;

            const onlinePresent = obj.online != null || obj.isOnline != null || obj.is_online != null;
            const online = onlinePresent ? Boolean(obj.online ?? obj.isOnline ?? obj.is_online) : undefined;

            const lastSeenRaw = obj.lastSeenAt ?? obj.last_seen_at ?? obj.lastSeen ?? obj.last_seen ?? null;
            const lastSeenAt = lastSeenRaw != null ? (toDate(lastSeenRaw) || undefined) : undefined;

            applyPresencePatch(did, [{ userId: Number(userId), isOnline: online, online, lastSeenAt }]);
        };

        const unsub = subscribe(topic, handlePresence);

        // join (optional)
        try {
            if (typeof publish === "function" && chatConfig.presenceJoin) {
                publish(chatConfig.presenceJoin, {
                    chatId: did,
                    clientId: presenceClientId,
                });
            }
        } catch {}

        return () => {
            try {
                unsub?.();
            } catch {}

            // leave (optional)
            try {
                if (typeof publish === "function" && chatConfig.presenceLeave) {
                    publish(chatConfig.presenceLeave, {
                        chatId: did,
                        clientId: presenceClientId,
                    });
                }
            } catch {}
        };
    }, [isAuthenticated, connected, activeDialogId, subscribe, publish, applyPresencePatch]);

    React.useEffect(() => {
        if (!isAuthenticated) return;
        if (!connected) return;
        if (!activeDialogId) return;
        if (typeof publish !== "function") return;
        if (!chatConfig.view) return;

        const did = String(activeDialogId);
        const clientId = presenceClientIdRef.current;

        const sendView = (active, force = false) => {
            const visible = pageVisible();
            const now = Date.now();

            // анти-спам: не шлём одно и то же чаще 1 раз в 2 сек, если не force
            const last = lastViewRef.current;
            const same =
                last.did === did && last.active === Boolean(active) && last.visible === Boolean(visible);

            if (!force && same && now - (last.ts || 0) < 2000) return;

            lastViewRef.current = { did, active: Boolean(active), visible: Boolean(visible), ts: now };

            try {
                publish(chatConfig.view, {
                    chatId: did,
                    clientId: String(clientId),
                    active: Boolean(active),
                    visible: Boolean(visible),
                });
            } catch {
                // ignore
            }
        };

        // ✅ старт: чат открыт
        sendView(true, true);

        // ✅ heartbeat
        const hbMs = Number(chatConfig.viewHeartbeatMs) || 20000;
        viewTimerRef.current = setInterval(() => sendView(true, false), hbMs);

        // ✅ visibility change => сразу пушим
        const onVis = () => sendView(true, true);
        document.addEventListener("visibilitychange", onVis);

        // (опционально, но полезно) при фокусе окна тоже обновим
        window.addEventListener("focus", onVis);

        return () => {
            if (viewTimerRef.current) clearInterval(viewTimerRef.current);
            viewTimerRef.current = null;

            document.removeEventListener("visibilitychange", onVis);
            window.removeEventListener("focus", onVis);

            // ✅ чат закрыли / переключили
            // если ws уже отвалился — TTL на бэке сам потухнет
            try {
                if (connected) sendView(false, true);
            } catch {}
        };
    }, [isAuthenticated, connected, activeDialogId, publish]);

    // WS subscriptions (messages/typing/dialogs)
    React.useEffect(() => {
        if (!isAuthenticated) return;

        const unsubs = [];
        const typingTimeouts = typingTimeoutsRef.current;
        const typingUserTimeouts = typingUserTimeoutsRef.current;
        const typingStopTimers = typingStopTimersRef.current;
        const typingLastSent = typingLastSentRef.current;
        const typingOn = typingOnRef.current;

        const handleTypingPayload = (payload, msg) => {
            dbg("[typing] raw", payload, msg?.headers);

            let p = payload;
            if (p && typeof p === "object" && typeof p.body === "string") p = tryParseJSON(p.body);
            if (typeof p === "string") p = tryParseJSON(p);
            if ((!p || typeof p !== "object") && msg?.body) p = tryParseJSON(msg.body);
            if (!p || typeof p !== "object") return;

            const payloadNode = tryParseJSON(p.payload);
            const dataNode = tryParseJSON(p.data);
            const envelope =
                (payloadNode && typeof payloadNode === "object" && payloadNode) ||
                (dataNode && typeof dataNode === "object" && dataNode) ||
                p;

            const did =
                envelope.dialogId ??
                envelope.chatId ??
                envelope.threadId ??
                envelope.dialog_id ??
                envelope.chat_id ??
                envelope.thread_id ??
                envelope.dialog?.id ??
                envelope.chat?.id ??
                envelope.thread?.id ??
                p.dialogId ??
                p.chatId ??
                p.threadId ??
                p.dialog_id ??
                p.chat_id ??
                p.thread_id ??
                p.dialog?.id ??
                p.chat?.id ??
                p.thread?.id;

            if (did == null) return;
            const didKey = String(did);

            const typingType = String(
                envelope.type ??
                    envelope.event ??
                    envelope.action ??
                    p.type ??
                    p.event ??
                    p.action ??
                    ""
            ).toUpperCase();

            let isTypingRaw =
                envelope.isTyping ??
                envelope.typing ??
                envelope.is_typing ??
                p.isTyping ??
                p.typing ??
                p.is_typing;
            if (isTypingRaw == null && typingType.includes("TYP")) {
                isTypingRaw = typingType.includes("STOP") ? false : true;
            }

            const isTyping =
                typeof isTypingRaw === "boolean"
                    ? isTypingRaw
                    : isTypingRaw == null
                        ? null
                        : Boolean(isTypingRaw);

            const from =
                envelope.fromUserId ??
                envelope.senderUserId ??
                envelope.userId ??
                envelope.from_user_id ??
                envelope.sender_user_id ??
                envelope.user_id ??
                envelope.user?.id ??
                p.fromUserId ??
                p.senderUserId ??
                p.userId ??
                p.from_user_id ??
                p.sender_user_id ??
                p.user_id ??
                p.user?.id;
            const uid = from != null ? Number(from) : null;

            const fromName =
                pickFirst(
                    envelope.fromUserName,
                    envelope.senderName,
                    envelope.userName,
                    envelope.authorName,
                    envelope.from_name,
                    envelope.sender_name,
                    envelope.user_name,
                    envelope.author_name,
                    envelope.user?.name,
                    envelope.user?.fullName,
                    p.fromUserName,
                    p.senderName,
                    p.userName,
                    p.authorName,
                    p.from_name,
                    p.sender_name,
                    p.user_name,
                    p.author_name,
                    p.user?.name,
                    p.user?.fullName
                ) || null;

            const sourceClientId =
                envelope.clientId ??
                envelope.client_id ??
                envelope.fromClientId ??
                envelope.from_client_id ??
                envelope.senderClientId ??
                envelope.sender_client_id ??
                p.clientId ??
                p.client_id ??
                p.fromClientId ??
                p.from_client_id ??
                p.senderClientId ??
                p.sender_client_id ??
                null;
            const localClientId = String(presenceClientIdRef.current || "");
            if (sourceClientId != null && localClientId && String(sourceClientId) === localClientId) return;
            if (uid != null && meId != null && Number(uid) === Number(meId)) return;

            const clearTypingTimersForDialog = (dialogId, except = new Set()) => {
                const keyPrefix = `${String(dialogId)}:`;
                for (const [k, t] of typingUserTimeouts.entries()) {
                    if (!String(k).startsWith(keyPrefix)) continue;
                    if (except.has(String(k))) continue;
                    clearTimeout(t);
                    typingUserTimeouts.delete(k);
                }
            };

            const setTypingUsersForDialog = (dialogId, users) => {
                const key = String(dialogId);
                const unique = [];
                const seen = new Set();
                (Array.isArray(users) ? users : []).forEach((entry) => {
                    const idRaw = entry?.id ?? entry?.userId ?? entry?.user_id ?? entry;
                    const idNum = Number(idRaw);
                    if (!Number.isFinite(idNum) || idNum <= 0) return;
                    if (meId != null && Number(idNum) === Number(meId)) return;
                    const idKey = String(idNum);
                    if (seen.has(idKey)) return;
                    seen.add(idKey);
                    const name =
                        pickFirst(entry?.name, entry?.fullName, entry?.userName, entry?.displayName, entry?.title) || null;
                    unique.push({ id: idNum, name });
                });

                if (!unique.length) {
                    setTypingUsersByDialog((prev) => {
                        if (!prev.has(key)) return prev;
                        const n = new Map(prev);
                        n.delete(key);
                        return n;
                    });
                    setTypingUsersMetaByDialog((prev) => {
                        if (!prev.has(key)) return prev;
                        const n = new Map(prev);
                        n.delete(key);
                        return n;
                    });
                    setTyping(dialogId, false);
                    clearTypingTimersForDialog(dialogId);
                    return;
                }

                setTypingUsersByDialog((prev) => {
                    const n = new Map(prev);
                    n.set(
                        key,
                        unique.map((x) => x.id)
                    );
                    return n;
                });
                setTypingUsersMetaByDialog((prev) => {
                    const n = new Map(prev);
                    n.set(key, unique);
                    return n;
                });
                setTyping(dialogId, true);

                const keep = new Set();
                unique.forEach((entry) => {
                    const tKey = `${key}:${entry.id}`;
                    keep.add(tKey);
                    const prevT = typingUserTimeouts.get(tKey);
                    if (prevT) clearTimeout(prevT);

                    const tt = setTimeout(() => {
                        setTypingUsersByDialog((prev) => {
                            const n = new Map(prev);
                            const cur = n.get(key) || [];
                            const next = cur.filter((x) => Number(x) !== Number(entry.id));
                            if (next.length) n.set(key, next);
                            else n.delete(key);
                            return n;
                        });
                        setTypingUsersMetaByDialog((prev) => {
                            const n = new Map(prev);
                            const cur = n.get(key) || [];
                            const next = cur.filter((x) => Number(x?.id) !== Number(entry.id));
                            if (next.length) n.set(key, next);
                            else n.delete(key);
                            return n;
                        });

                        setTypingUsersByDialog((prev) => {
                            const hasAny = (prev.get(key) || []).length > 0;
                            if (!hasAny) setTyping(dialogId, false);
                            return prev;
                        });

                        typingUserTimeouts.delete(tKey);
                    }, 2600);

                    typingUserTimeouts.set(tKey, tt);
                });
                clearTypingTimersForDialog(dialogId, keep);
            };

            const addTyper = (dialogId, userId, userName = null) => {
                const idNum = Number(userId);
                if (!Number.isFinite(idNum) || idNum <= 0) return;
                const key = String(dialogId);

                setTypingUsersByDialog((prev) => {
                    const n = new Map(prev);
                    const cur = n.get(key) || [];
                    if (cur.some((x) => Number(x) === Number(idNum))) return prev;
                    n.set(key, [...cur, idNum]);
                    return n;
                });

                setTypingUsersMetaByDialog((prev) => {
                    const n = new Map(prev);
                    const cur = n.get(key) || [];
                    const ix = cur.findIndex((x) => Number(x?.id) === Number(idNum));
                    if (ix >= 0) {
                        if (!userName || cur[ix]?.name) return prev;
                        const copy = [...cur];
                        copy[ix] = { ...copy[ix], name: userName };
                        n.set(key, copy);
                        return n;
                    }
                    n.set(key, [...cur, { id: idNum, name: userName || null }]);
                    return n;
                });
            };

            const removeTyper = (dialogId, userId) => {
                const idNum = Number(userId);
                if (!Number.isFinite(idNum) || idNum <= 0) return;
                const key = String(dialogId);

                setTypingUsersByDialog((prev) => {
                    const n = new Map(prev);
                    const cur = n.get(key) || [];
                    const next = cur.filter((x) => Number(x) !== Number(idNum));
                    if (next.length) n.set(key, next);
                    else n.delete(key);
                    if (!next.length) setTyping(dialogId, false);
                    return n;
                });

                setTypingUsersMetaByDialog((prev) => {
                    const n = new Map(prev);
                    const cur = n.get(key) || [];
                    const next = cur.filter((x) => Number(x?.id) !== Number(idNum));
                    if (next.length) n.set(key, next);
                    else n.delete(key);
                    return n;
                });
            };

            const typingUsersRaw =
                envelope.typingUsers ??
                envelope.typers ??
                envelope.usersTyping ??
                envelope.typing_users ??
                envelope.typing_users_ids ??
                p.typingUsers ??
                p.typers ??
                p.usersTyping ??
                p.typing_users ??
                p.typing_users_ids ??
                null;

            if (Array.isArray(typingUsersRaw)) {
                if (isTyping === false) {
                    setTypingUsersForDialog(did, []);
                    return;
                }
                setTypingUsersForDialog(did, typingUsersRaw);
                return;
            }

            if (uid == null) {
                if (isTyping === false) {
                    setTypingUsersForDialog(did, []);
                    const prevT = typingTimeoutsRef.current.get(didKey);
                    if (prevT) clearTimeout(prevT);
                    typingTimeoutsRef.current.delete(didKey);
                    return;
                }

                setTyping(did, true);
                const prevT = typingTimeoutsRef.current.get(didKey);
                if (prevT) clearTimeout(prevT);

                const tt = setTimeout(() => {
                    setTyping(did, false);
                    typingTimeoutsRef.current.delete(didKey);
                }, 2600);

                typingTimeoutsRef.current.set(didKey, tt);
                return;
            }

            const tKey = `${didKey}:${uid}`;
            if (isTyping === false) {
                removeTyper(did, uid);
                const prevT = typingUserTimeouts.get(tKey);
                if (prevT) clearTimeout(prevT);
                typingUserTimeouts.delete(tKey);
                return;
            }

            addTyper(did, uid, fromName);
            setTyping(did, true);

            const prevT = typingUserTimeouts.get(tKey);
            if (prevT) clearTimeout(prevT);

            const tt = setTimeout(() => {
                removeTyper(did, uid);
                typingUserTimeouts.delete(tKey);
            }, 2600);

            typingUserTimeouts.set(tKey, tt);
        };


        /**
         * ✅ FIX: READ обработчик — только по конкретным messageIds/messageId или upToId
         * И обновляем НЕ "всем readAt", а флаг "прочитано другой стороной" для МОИХ сообщений.
         */
        const handleRead = (payloadRaw) => {
            let obj = payloadRaw;
            if (obj && typeof obj === "object" && typeof obj.body === "string") obj = tryParseJSON(obj.body);
            if (typeof obj === "string") obj = tryParseJSON(obj);
            if (!obj || typeof obj !== "object") return;

            // ✅ сервер может слать CHAT_READ / MESSAGE_READ / CHAT_MESSAGE_READ и т.п.
            const type = String(obj.type || obj.event || obj.action || "").toUpperCase();
            if (type && !type.includes("READ")) return;

            const did =
                obj.chatId ??
                obj.dialogId ??
                obj.threadId ??
                obj.chat_id ??
                obj.dialog_id ??
                obj.thread_id;

            if (did == null) return;

            const readerId =
                obj.fromUserId ??
                obj.readerUserId ??
                obj.userId ??
                obj.from_user_id ??
                obj.reader_user_id ??
                obj.user_id ??
                null;

            if (readerId == null) return;

            // если это я — игнор
            if (meId != null && Number(readerId) === Number(meId)) return;

            // ids: поддержим больше вариантов
            const idsRaw =
                obj.messageIds ??
                obj.message_ids ??
                obj.readMessageIds ??
                obj.read_message_ids ??
                obj.ids ??
                (obj.messageId != null ? [obj.messageId] : null) ??
                (obj.message_id != null ? [obj.message_id] : null);

            let ids = [];
            if (Array.isArray(idsRaw)) {
                ids = idsRaw.map((x) => Number(x)).filter((x) => Number.isFinite(x) && x > 0);
            }

            // upTo: тоже поддержим больше ключей
            const upToId = Number(
                obj.upToMessageId ??
                obj.up_to_message_id ??
                obj.lastReadMessageId ??
                obj.last_read_message_id ??
                obj.cursorMessageId ??
                obj.cursor_message_id ??
                obj.readToMessageId ??
                obj.read_to_message_id ??
                NaN
            );
            const hasUpTo = Number.isFinite(upToId) && upToId > 0;

            // без конкретики — ничего не делаем
            if (ids.length === 0 && !hasUpTo) return;

            const readAtDt = toDate(obj.readAt ?? obj.read_at) || new Date();

            // ✅ какой флаг ставим (прочитано ДРУГОЙ стороной для МОИХ сообщений)
            // если я психолог — другая сторона клиент, и наоборот
            const otherFlag =
                isPsychologistLikeRole(me?.role)
                    ? "isReadByClient"
                    : "isReadByPsychologist";

            setMessagesByDialog((prev) => {
                const n = new Map(prev);
                const arr = n.get(String(did)) || [];
                if (!arr.length) return prev;

                const idsSet = new Set(ids);

                const next = arr.map((m) => {
                    const mine = meId != null && m?.fromUserId != null && Number(m.fromUserId) === Number(meId);
                    if (!mine) return m;
                    if (m?.deleted || m?.isDeleted) return m;
                    if (m?.status === "failed") return m;

                    const mid = Number(m?.id);
                    if (!Number.isFinite(mid) || mid <= 0) return m;

                    const shouldMark = idsSet.size ? idsSet.has(mid) : hasUpTo ? mid <= upToId : false;
                    if (!shouldMark) return m;

                    // уже отмечено — не трогаем
                    if (m?.[otherFlag] === true || m?.readByOther === true) return m;

                    return {
                        ...m,
                        // ✅ если UI где-то смотрит это поле — даём его тоже
                        readByOther: true,
                        [otherFlag]: true,
                        readAt: m.readAt ?? readAtDt.toISOString(),
                    };
                });

                n.set(String(did), next);
                return n;
            });

            refreshDialogsDebounced();
        };

        const handleReactions = (obj) => {
            const did = obj.dialogId ?? obj.chatId ?? obj.dialog_id ?? obj.chat_id ?? obj.message?.dialogId ?? obj.message?.chatId ?? null;
            const mid = obj.messageId ?? obj.message_id ?? obj.message?.id ?? obj.message?.messageId ?? obj.id ?? null;
            if (did == null || mid == null) return;

            const rawReactionsFromPayload =
                obj.reactions ??
                obj.reactionSummary ??
                obj.reaction_summary ??
                obj.message?.reactions ??
                obj.message?.reactionSummary ??
                obj.message?.reaction_summary ??
                null;
            const rawReactions = rawReactionsFromPayload ?? [];
            const prevMessage = getMessageSnapshot(did, mid);
            const prevReactions = Array.isArray(prevMessage?.reactions) ? prevMessage.reactions : [];
            const actorId = pickReactionActorId(obj);
            const clientId = pickReactionClientId(obj);
            const fromMe = actorId != null && meId != null && Number(actorId) === Number(meId);
            const fromThisClient = clientId && String(clientId) === String(presenceClientIdRef.current || "");
            const directEmoji = obj?.emoji ?? obj?.reaction ?? obj?.e ?? obj?.reactionEmoji ?? obj?.reaction_emoji ?? null;
            const explicitAdd = obj?.add ?? obj?.added ?? obj?.isAdded ?? obj?.is_added ?? obj?.active ?? null;

            if (fromThisClient && directEmoji && rawReactionsFromPayload == null) {
                return;
            }

            let reactions = normReactions(rawReactions, meId);

            // Some WS backends send only { messageId, emoji, add } without full summary.
            // Keep the receiver UI in sync anyway, instead of wiping reaction badges.
            if (!reactions.length && directEmoji) {
                reactions = upsertReactionSummary(prevReactions, String(directEmoji), fromMe, explicitAdd !== false ? 1 : -1);
            }

            const fxEmoji = pickReactionEmojiForFx(obj, prevReactions, reactions);

            applyReactionsSet(did, mid, reactions);

            // Receiver-side Telegram-like effect: when another participant adds a reaction,
            // the visual burst is triggered after React has drawn the updated reaction badge.
            if (fxEmoji && !fromMe && !fromThisClient) {
                window.setTimeout(() => {
                    dispatchReactionEffect({ dialogId: String(did), messageId: String(mid), emoji: fxEmoji });
                }, 0);
            }
        };

        unsubs.push(
            subscribe(chatConfig.inbox, (payload, msg) => {

                let obj = payload;

                if (obj && typeof obj === "object" && typeof obj.body === "string") obj = tryParseJSON(obj.body);
                if (typeof obj === "string") obj = tryParseJSON(obj);
                if ((!obj || typeof obj !== "object") && msg?.body) obj = tryParseJSON(msg.body);
                if (!obj) return;

                const type = String(obj.type || obj.event || obj.action || "").toUpperCase();

                if (type === "PRESENCE") {
                    return;
                }
                if (type === "DIALOGS_CHANGED") {
                    refreshDialogsDebounced();
                    return;
                }

                if (type.includes("READ")) {
                    handleRead(obj);
                    return;
                }

                if (obj.isTyping != null || obj.typing != null || obj.is_typing != null || type.includes("TYP")) {
                    handleTypingPayload(obj, msg);
                    return;
                }

                if (type.includes("REACTION") || type.includes("REACT")) {
                    handleReactions(obj);
                    return;
                }

                if (type.includes("DELETED") || type.includes("DELETE") || type.includes("REMOVE")) {
                    const did = obj.dialogId ?? obj.chatId ?? obj.threadId ?? obj.dialog_id ?? obj.chat_id;
                    const mid = obj.messageId ?? obj.id ?? obj.message_id;
                    if (did != null && mid != null) applyDelete(did, mid);
                    refreshDialogsDebounced();
                    return;
                }

                if (type.includes("EDITED") || type.includes("EDIT") || type.includes("UPDATE")) {
                    const did = obj.dialogId ?? obj.chatId ?? obj.threadId ?? obj.dialog_id ?? obj.chat_id;
                    const mid = obj.messageId ?? obj.id ?? obj?.message?.id ?? obj.message_id;
                    if (did == null || mid == null) return;

                    const patchMsg = obj.message ? normMessage(obj.message, did, meId) : null;
                    applyEdit(did, mid, {
                        text: patchMsg?.text ?? obj.content ?? obj.text ?? "",
                        isEdited: true,
                        editedAt: obj.editedAt ?? obj.edited_at ?? obj.lastModified ?? new Date().toISOString(),
                        lastModified: obj.lastModified ?? new Date().toISOString(),
                        replyTo: patchMsg?.replyTo ?? undefined,
                        replyToKey: patchMsg?.replyToKey ?? undefined,
                        replyToMessageId: patchMsg?.replyToMessageId ?? undefined,
                        replyToClientId: patchMsg?.replyToClientId ?? undefined,
                        reactions: patchMsg?.reactions ?? undefined,
                    });
                    refreshDialogsDebounced();
                    return;
                }

                const msg2 = normMessage(obj, obj.chatId ?? obj.dialogId ?? obj.chat_id ?? obj.dialog_id, meId);
                if (msg2) {
                    const did = msg2?.dialogId == null ? "" : String(msg2.dialogId);
                    const activeDid = activeDialogIdRef.current == null ? "" : String(activeDialogIdRef.current);
                    const fromOther = meId != null && msg2?.fromUserId != null && Number(msg2.fromUserId) !== Number(meId);
                    const activeIncoming = Boolean(did && activeDid && did === activeDid && fromOther);

                    appendMessage(msg2);

                    // Не делаем мгновенный refresh списка по открытому диалогу: он часто возвращает stale unread
                    // до завершения read-запроса и визуально возвращает непрочитанные назад.
                    if (activeIncoming) {
                        if (typeof window !== "undefined" && typeof window.setTimeout === "function") {
                            window.setTimeout(() => refreshDialogsDebounced(), 1200);
                        } else {
                            refreshDialogsDebounced();
                        }
                    } else {
                        refreshDialogsDebounced();
                    }
                    return;
                }

                upsertDialog(obj);
            })
        );

        if (chatConfig.typingSub) {
            unsubs.push(subscribe(chatConfig.typingSub, handleTypingPayload));
        }

        if (chatConfig.dialogsSub) {
            unsubs.push(
                subscribe(chatConfig.dialogsSub, (payload, msg) => {
                    let obj = payload;
                    if (obj && typeof obj === "object" && typeof obj.body === "string") obj = tryParseJSON(obj.body);
                    if (typeof obj === "string") obj = tryParseJSON(obj);
                    if ((!obj || typeof obj !== "object") && msg?.body) obj = tryParseJSON(msg.body);
                    if (!obj) return;

                    const type = String(obj.type || "").toUpperCase();
                    if (type === "DIALOGS_CHANGED") {
                        refreshDialogsDebounced();
                        return;
                    }

                    const arr = Array.isArray(obj) ? obj : obj.items || obj.content || obj.data || null;
                    if (Array.isArray(arr)) {
                        const incoming = arr.map(normDialog).filter(Boolean);
                        setDialogs((prev) => {
                            const map = new Map(prev.map((d) => [d.id, d]));
                            const merged = incoming.map((d) => mergeDialogSafe(map.get(d.id), d));
                            for (const p2 of prev) if (!merged.some((x) => x.id === p2.id)) merged.push(p2);
                            return merged.sort(sortByUpdatedDesc);
                        });
                    } else {
                        upsertDialog(obj);
                    }
                })
            );
        }

        return () => {
            for (const u of unsubs) {
                try {
                    u?.();
                } catch {}
            }

            for (const t of typingTimeouts.values()) clearTimeout(t);
            typingTimeouts.clear();

            for (const t of typingUserTimeouts.values()) clearTimeout(t);
            typingUserTimeouts.clear();

            for (const t of typingStopTimers.values()) clearTimeout(t);
            typingStopTimers.clear();

            typingLastSent.clear();
            typingOn.clear();

            if (dialogsRefreshTimerRef.current) clearTimeout(dialogsRefreshTimerRef.current);
            dialogsRefreshTimerRef.current = null;
        };
    }, [
        isAuthenticated,
        subscribe,
        appendMessage,
        applyDelete,
        applyEdit,
        applyReactionsSet,
        getMessageSnapshot,
        upsertDialog,
        setTyping,
        meId,
        refreshDialogsDebounced,
        applyPresencePatch,
        me?.role,
        toast,
    ]);

    /**
     * ✅ FIX: openDialog больше НЕ обнуляет unreadCount "на вход"
     * unreadCount уменьшается через markMessagesRead, когда реально увидел сообщения.
     * + подтягиваем dialog details (type/members) сразу.
     */
    const openDialog = React.useCallback(
        (dialogId) => {
            const did = dialogId == null ? "" : String(dialogId).trim();
            // ✅ allow closing dialog (useful on mobile: back to list)
            if (!did) {
                setActiveDialogId(null);
                return;
            }

            setActiveDialogId(did);

            loadDialogDetails(did, { force: false });

            const loaded = historyLoadedByDialog.get(did) === true;
            if (!loaded) loadMessages(did, { append: false });
        },
        [loadMessages, historyLoadedByDialog, loadDialogDetails]
    );

    const refreshDialogDetails = React.useCallback(
        async (dialogId, { force = true } = {}) => {
            const did = dialogId == null ? "" : String(dialogId).trim();
            if (!did) return;
            await loadDialogDetails(did, { force: Boolean(force) });
        },
        [loadDialogDetails]
    );

    const addDialogMember = React.useCallback(
        async (dialogId, userId) => {
            const did = dialogId == null ? "" : String(dialogId).trim();
            const uid = Number(userId);

            if (!did) throw new Error("Не указан диалог");
            if (!Number.isFinite(uid) || uid <= 0) throw new Error("Некорректный пользователь");
            if (typeof chatApi.addMember !== "function") throw new Error("Добавление участников не поддерживается");

            await chatApi.addMember(did, uid);
            await loadDialogDetails(did, { force: true });
            refreshDialogsDebounced();
            return { ok: true };
        },
        [loadDialogDetails, refreshDialogsDebounced]
    );

    const removeDialogMember = React.useCallback(
        async (dialogId, userId) => {
            const did = dialogId == null ? "" : String(dialogId).trim();
            const uid = Number(userId);

            if (!did) throw new Error("Не указан диалог");
            if (!Number.isFinite(uid) || uid <= 0) throw new Error("Некорректный пользователь");
            if (typeof chatApi.removeMember !== "function") throw new Error("Удаление участников не поддерживается");

            await chatApi.removeMember(did, uid);
            await loadDialogDetails(did, { force: true });
            refreshDialogsDebounced();
            return { ok: true };
        },
        [loadDialogDetails, refreshDialogsDebounced]
    );

    const sendMessage = React.useCallback(
        async ({ dialogId, text, files, replyToMessageId, replyToClientId, replyToKey, replyTo }) => {
            const did = String(dialogId);
            const clientId = uid();
            const cleanText = sanitizeText(text ?? "");
            const hasFiles = Array.isArray(files) && files.length > 0;
            const trimmedText = cleanText.trim();
            const optimisticSticker = parseChatStickerPayload(cleanText);
            const optimisticContentCard = parseChatContentCardPayload(cleanText);
            const optimisticText = getVisibleChatText(cleanText);

            if (!hasFiles && !trimmedText) return;

            const finalReplyToMessageId = replyToMessageId != null ? String(replyToMessageId) : null;
            const finalReplyToClientId = replyToClientId != null ? String(replyToClientId) : null;
            const finalReplyToKey = replyToKey != null ? String(replyToKey) : finalReplyToMessageId || finalReplyToClientId;

            const optimistic = {
                id: `tmp_${clientId}`,
                clientId,
                dialogId: did,
                fromUserId: meId == null ? null : Number(meId),
                text: optimisticText,
                content: optimisticText,
                rawContent: cleanText,
                sticker: optimisticSticker,
                contentCard: optimisticContentCard,
                messageType: optimisticSticker ? "sticker" : optimisticContentCard ? "content_card" : null,
                createdAt: new Date(),
                status: "pending",
                readAt: null,
                readByMe: true,
                isReadByPsychologist: isPsychologistLikeRole(me?.role) ? true : null,
                isReadByClient: isPsychologistLikeRole(me?.role) ? null : true,

                replyToMessageId: finalReplyToMessageId,
                replyToClientId: finalReplyToClientId,
                replyToKey: finalReplyToKey,

                replyTo: finalReplyToMessageId
                    ? {
                        id: String(finalReplyToMessageId),
                        messageId: String(finalReplyToMessageId),
                        fromUserId: replyTo?.fromUserId ?? null,
                        fromName: replyTo?.fromName ?? replyTo?.fromUserName ?? null,
                        text: sanitizeText(replyTo?.text ?? replyTo?.content ?? "", 500),
                        createdAt: replyTo?.createdAt ?? null,
                    }
                    : null,

                reactions: [],
                attachments: (files || []).map((f) => ({
                    id: uid(),
                    kind: isImageUploadFile(f) ? "image" : "file",
                    url: URL.createObjectURL(f),
                    name: f.name,
                    size: f.size,
                    mime: f.type,
                    _local: true,
                    _file: f,
                })),
            };

            appendMessage(optimistic);

            const canWs = connected && typeof publish === "function";

            if (hasFiles) {
                try {
                    const saved = await chatApi.sendMessage(did, {
                        text: cleanText,
                        clientId,
                        files,
                        replyToMessageId: finalReplyToMessageId,
                        replyToClientId: finalReplyToClientId,
                        replyToKey: finalReplyToKey,
                    });
                    const msg = normMessage(saved, did, meId);
                    if (msg) appendMessage(msg);
                } catch (e) {
                    toast.error(e?.message || "Не удалось отправить сообщение");
                    applyEditByClientId(did, clientId, { status: "failed" });
                }
                return;
            }

            const ok = canWs
                ? publish(chatConfig.send, {
                    chatId: did,
                    content: cleanText,
                    clientId,
                    replyToMessageId: finalReplyToMessageId ? Number(finalReplyToMessageId) : null,
                    replyToId: finalReplyToMessageId ? Number(finalReplyToMessageId) : null,
                    replyToClientId: finalReplyToClientId,
                    replyToKey: finalReplyToKey,
                })
                : false;

            if (ok) {
                clearPendingAck(did, clientId);
                const ackKey = getPendingAckKey(did, clientId);
                const ackTimer = setTimeout(async () => {
                    pendingAckTimersRef.current.delete(ackKey);
                    const snap = getMessageSnapshot(did, clientId);
                    if (!snap || snap.status !== "pending") return;

                    try {
                        const saved = await chatApi.sendMessage(did, {
                            text: cleanText,
                            clientId,
                            replyToMessageId: finalReplyToMessageId,
                            replyToClientId: finalReplyToClientId,
                            replyToKey: finalReplyToKey,
                        });
                        const msg = normMessage(saved, did, meId);
                        if (msg) appendMessage(msg);
                        else applyEditByClientId(did, clientId, { status: "failed" });
                    } catch {
                        applyEditByClientId(did, clientId, { status: "failed" });
                        toast.error("Сообщение не отправлено. Проверьте соединение и повторите.");
                    }
                }, CHAT_ACK_TIMEOUT_MS);

                pendingAckTimersRef.current.set(ackKey, ackTimer);
                return;
            }

            try {
                const saved = await chatApi.sendMessage(did, {
                    text: cleanText,
                    clientId,
                    replyToMessageId: finalReplyToMessageId,
                    replyToClientId: finalReplyToClientId,
                    replyToKey: finalReplyToKey,
                });
                const msg = normMessage(saved, did, meId);
                if (msg) appendMessage(msg);
            } catch (e) {
                toast.error(e?.message || "Не удалось отправить сообщение");
                applyEditByClientId(did, clientId, { status: "failed" });
            }
        },
        [
            appendMessage,
            connected,
            publish,
            meId,
            me?.role,
            toast,
            applyEditByClientId,
            clearPendingAck,
            getPendingAckKey,
            getMessageSnapshot,
        ]
    );

    const reactToMessage = React.useCallback(
        async ({ dialogId, messageId, emoji, add }) => {
            const did = String(dialogId);
            const mid = String(messageId);
            if (!emoji || !mid) return null;

            const snap = applyReactionOptimistic(did, mid, String(emoji), add);

            const canWs = connected && typeof publish === "function" && Boolean(chatConfig.react);

            const payload = buildReactionTogglePayload({
                reaction: String(emoji),
                add,
                clientId: String(presenceClientIdRef.current || ""),
                targetType: REACTION_TARGET_TYPES.CHAT_MESSAGE,
                targetId: mid,
                extra: {
                    dialogId: Number.isFinite(Number(did)) ? Number(did) : did,
                    chatId: Number.isFinite(Number(did)) ? Number(did) : did,
                    messageId: Number(mid),
                },
            });

            const ok = canWs ? publish(chatConfig.react, payload) : false;
            if (ok) return { ok: true };

            try {
                const res = await chatApi.reactToMessage(mid, {
                    emoji: String(emoji),
                    add: add == null ? undefined : Boolean(add),
                    clientId: String(presenceClientIdRef.current || ""),
                });
                if (res?.reactions) {
                    const reactions = normReactions(res.reactions, meId);
                    applyReactionsSet(did, mid, reactions);
                }
                return res;
            } catch (e) {
                toast.error(e?.message || "Не удалось поставить реакцию");
                if (snap?.prev) applyReactionsSet(did, mid, snap.prev);
                return null;
            }
        },
        [applyReactionOptimistic, connected, publish, toast, applyReactionsSet, meId]
    );

    const notifyTyping = React.useCallback(
        (dialogId) => {
            const did = String(dialogId);
            if (!connected || typeof publish !== "function") return;

            const sendTyping = (flag) => {
                publish(chatConfig.typing, {
                    chatId: did,
                    clientId: String(presenceClientIdRef.current || ""),
                    typing: Boolean(flag),
                    isTyping: Boolean(flag),
                });
            };

            const now = Date.now();
            const last = typingLastSentRef.current.get(did) || 0;

            if (now - last > 900) {
                sendTyping(true);
                typingLastSentRef.current.set(did, now);
                typingOnRef.current.set(did, true);
            }

            const prevStop = typingStopTimersRef.current.get(did);
            if (prevStop) clearTimeout(prevStop);

            const t = setTimeout(() => {
                if (typingOnRef.current.get(did) === true) {
                    sendTyping(false);
                    typingOnRef.current.set(did, false);
                }
            }, 1200);

            typingStopTimersRef.current.set(did, t);
        },
        [connected, publish]
    );

    const editMessage = React.useCallback(
        async ({ dialogId, messageId, text, files = [], keepAttachmentIds = [], removeAttachmentIds = [] }) => {
            const did = String(dialogId);
            const mid = String(messageId);
            const cleanText = sanitizeText(text ?? "");
            const filesToUpload = Array.isArray(files) ? files : [];
            const keepIds = Array.isArray(keepAttachmentIds) ? keepAttachmentIds.map(String) : [];
            const removeIds = Array.isArray(removeAttachmentIds) ? removeAttachmentIds.map(String) : [];
            if (!cleanText.trim() && !keepIds.length && !filesToUpload.length) return;

            const before = getMessageSnapshot(did, mid);
            const keepSet = new Set(keepIds);
            const nextAttachments = [
                ...((Array.isArray(before?.attachments) ? before.attachments : []).filter((attachment) => {
                    const key = attachment?.id ?? attachment?.attachmentId ?? attachment?.url ?? attachment?.name;
                    if (!key) return true;
                    if (removeIds.includes(String(key))) return false;
                    if (keepSet.size) return keepSet.has(String(key));
                    return true;
                })),
                ...filesToUpload.map((file) => ({
                    id: uid(),
                    kind: isImageUploadFile(file) ? "image" : "file",
                    url: URL.createObjectURL(file),
                    name: file?.name || "Файл",
                    size: file?.size || 0,
                    mime: file?.type || "",
                    _local: true,
                    _file: file,
                })),
            ];

            applyEdit(did, mid, {
                text: cleanText,
                attachments: nextAttachments,
                editedAt: new Date().toISOString(),
                isEdited: true,
                lastModified: new Date().toISOString(),
            });

            const canWsEdit =
                !filesToUpload.length &&
                !removeIds.length &&
                connected &&
                typeof publish === "function";

            const ok = canWsEdit
                ? publish(chatConfig.edit, { messageId: Number(mid), content: cleanText })
                : false;

            if (ok) return;

            try {
                const saved = await chatApi.editMessage(mid, {
                    text: cleanText,
                    files: filesToUpload,
                    keepAttachmentIds: keepIds,
                    removeAttachmentIds: removeIds,
                });
                const normalized = normMessage(saved, did, meId);
                if (normalized) {
                    applyEdit(did, mid, {
                        text: normalized.text,
                        attachments: normalized.attachments,
                        editedAt: normalized.editedAt ?? new Date().toISOString(),
                        isEdited: true,
                        lastModified: normalized.lastModified ?? new Date().toISOString(),
                    });
                }
            } catch (e) {
                if (before) {
                    applyEdit(did, mid, {
                        text: before.text,
                        attachments: before.attachments,
                        editedAt: before.editedAt ?? null,
                        isEdited: Boolean(before.isEdited),
                        lastModified: before.lastModified ?? null,
                    });
                }
                toast.error(e?.message || "Не удалось изменить сообщение");
            }
        },
        [applyEdit, connected, publish, getMessageSnapshot, meId, toast]
    );

    const deleteMessage = React.useCallback(
        async ({ dialogId, messageId }) => {
            const did = String(dialogId);
            const mid = String(messageId);
            const before = getMessageSnapshot(did, mid);
            applyDelete(did, mid);

            const ok =
                connected && typeof publish === "function"
                    ? publish(chatConfig.remove, { messageId: Number(mid) })
                    : false;

            if (ok) return;

            try {
                await chatApi.deleteMessage(mid);
            } catch (e) {
                if (before) {
                    const wasDeleted = Boolean(before.deleted || before.isDeleted);
                    patchMessageLocal(did, mid, {
                        ...before,
                        deleted: wasDeleted,
                        isDeleted: wasDeleted,
                        deletedAt: wasDeleted ? before.deletedAt ?? before.deletedWhen ?? null : null,
                        deletedWhen: wasDeleted ? before.deletedWhen ?? before.deletedAt ?? null : null,
                        text: before.text ?? before.content ?? "",
                        content: before.content ?? before.text ?? "",
                        attachments: Array.isArray(before.attachments) ? before.attachments : [],
                    });
                }
                toast.error(e?.message || "Не удалось удалить сообщение");
            }
        },
        [applyDelete, connected, publish, getMessageSnapshot, patchMessageLocal, toast]
    );

    React.useEffect(() => {
        const ackTimers = pendingAckTimersRef.current;
        const messagesMap = messagesByDialogRef.current;
        return () => {
            for (const timer of ackTimers.values()) {
                clearTimeout(timer);
            }
            ackTimers.clear();

            for (const arr of messagesMap.values()) {
                for (const msg of arr || []) revokeLocalAttachments(msg);
            }
        };
    }, []);

    // ✅ activeDialog = merge(preview, details)
    const activeDialog = React.useMemo(() => {
        if (!activeDialogId) return null;
        const did = String(activeDialogId);
        const preview = dialogs.find((d) => d.id === did) || null;
        const details = dialogDetailsById.get(did) || null;
        return mergeDialogSafe(preview, details);
    }, [dialogs, dialogDetailsById, activeDialogId]);

    const activeMessages = React.useMemo(
        () => (activeDialogId ? messagesByDialog.get(String(activeDialogId)) || [] : []),
        [messagesByDialog, activeDialogId]
    );

    const activeLoading = React.useMemo(
        () => (activeDialogId ? messagesLoading.get(String(activeDialogId)) || false : false),
        [messagesLoading, activeDialogId]
    );

    const loadMoreActive = React.useCallback(async () => {
        const did = activeDialogId;
        if (!did) return;
        const cursor = messagesCursor.get(String(did));
        if (!cursor) return;
        await loadMessages(String(did), { append: true });
    }, [activeDialogId, messagesCursor, loadMessages]);

    const activeTyping = Boolean(activeDialog?.typing);
    const activeTypingText = activeTyping ? "Печатает…" : "";

    const activeTypingUsers = React.useMemo(() => {
        if (!activeDialogId) return [];
        return typingUsersByDialog.get(String(activeDialogId)) || [];
    }, [typingUsersByDialog, activeDialogId]);


    return {
        meId,
        dialogs,
        dialogsLoading,
        dialogsError,

        activeDialogId,
        activeDialog,
        activeTyping,
        activeTypingText,
        activeTypingUsers,
        typingUsersByDialog,
        typingUsersMetaByDialog,

        // legacy (можешь убрать после перевода UI на per-message)
        markDialogReadSeen,

        // ✅ NEW
        markMessagesRead,

        openDialog,
        refreshDialogDetails,

        messages: activeMessages,
        messagesLoading: activeLoading,
        loadMore: loadMoreActive,

        sendMessage,
        notifyTyping,
        editMessage,
        deleteMessage,

        patchMessageLocal,

        reactToMessage,
        reactMessage: reactToMessage,
        addDialogMember,
        removeDialogMember,

        connected,
    };
}
